:host {
  --value-font-family: var(--stzh-font-family-medium);
  --value-font-weight: var(--stzh-font-weight-medium);
  --value-color: inherit;
  --action-display: flex;
  --leading-icon-color: #{$colorGrey90};
  --nested-display: var(--stzh-datalist-item-nested-display, block);
  --wrapper-display: var(--stzh-datalist-item-wrapper-display, contents);
  --vertical-wrapper-padding: var(
    --stzh-datalist-item-vertical-wrapper-padding,
    #{space("xsmall")}
  );
  --text-overflow: clip;
  --white-space: normal;
  --overflow: visible;
  --width: initial;
  --max-width: initial;

  display: contents;

  &[href]:not([href=""]),
  &[a11y-expanded],
  &[a11y-controls] {
    --value-color: #{$linkColor};
    --value-hover-color: #{$linkHoverColor};
  }
}

:host(.value-truncate) {
  --text-overflow: ellipsis;
  --white-space: nowrap;
  --overflow: hidden;
  --max-width: 100%;
}

.stzh-datalist-item {
  position: relative;
  text-decoration-line: none;
  display: var(--wrapper-display);
  align-items: center;

  &__nested {
    display: contents;
  }

  &__nested-inner {
    display: var(--nested-display);
    grid-column: var(--full-grid-column);
  }

  &__vhidden {
    @include visuallyhidden;
  }

  &__wrapper {
    flex-grow: 1;
    display: grid;
  }

  &__leading,
  &__value,
  &__label,
  &__meta,
  &__action-list {
    color: inherit;
    display: flex;
    text-align: left;
    appearance: none;
    font-family: inherit;
    background: transparent;
    border: none;
    padding: 0;
    text-decoration-line: none;

    &.is-button,
    &.is-link {
      cursor: pointer;
    }
  }

  &__leading {
    flex-shrink: 0;

    &:not(:empty) {
      padding-right: space("xsmall");
    }
  }

  &__leading-icon-container {
    color: var(--leading-icon-color);
    transition-property: color, background-color;
    transition-duration: $baseTransitionAnimationSpeed;
  }

  &__value-wrapper {
    display: flex;
    align-items: center;
    gap: space("xsmall");
    color: var(--value-color);
    transition: color $baseTransitionAnimationSpeed;
  }

  &__value {
    flex-grow: 1;
    flex-direction: column;
    min-width: 0;
  }

  &__value-text {
    --color: initial;
    min-width: 0;

    @include fontCurve("p1");
    font-family: var(--value-font-family);
    font-weight: var(--value-font-weight);
    width: auto;
  }

  &__label,
  &__meta {
    @include fontCurve("caption");
  }

  &__meta {
    justify-content: flex-end;
  }

  &__meta-text {
    display: flex;
    gap: space("xsmall");
    color: $colorGrey70;
  }

  &__label {
    color: $colorGrey90;
  }

  &__action-list {
    display: var(--action-display);
    justify-content: center;
    text-align: center;

    &:not(:empty) {
      padding-left: space("medium");
    }
  }

  &__action {
    &.is-non-interactive {
      display: flex;
      justify-content: center;
      align-items: center;
      width: $formInputHeightSmall;
      height: $formInputHeightSmall;
    }
  }

  &__interactive {
    position: absolute;
    top: 0;
    left: 0;
  }

  &__readspeaker {
    width: fit-content;
  }

  /* Button variant */

  &--is-button &__icon.is-open {
    display: none;
  }

  &--is-button#{&}--is-expanded &__icon {
    &.is-closed {
      display: none;
    }

    &.is-open {
      display: inline-flex;
    }
  }

  /* Interactive variant */

  &--is-interactive &__value-text {
    text-decoration-line: underline;
  }

  &--is-interactive:hover &__value-wrapper {
    color: var(--value-hover-color);
  }

  /* Vertical Variant */

  &--direction-vertical &__wrapper {
    flex-grow: inherit;
  }

  &--direction-vertical &__leading,
  &--direction-vertical &__wrapper,
  &--direction-vertical &__action-list {
    &:not(:empty) {
      padding-top: var(--vertical-wrapper-padding);
      padding-bottom: var(--vertical-wrapper-padding);
    }
  }

  &--direction-vertical &__meta {
    flex-direction: column;
    align-items: flex-start;
  }

  /* Horizontal / Table Variant */

  &--direction-horizontal &__leading,
  &--direction-horizontal &__value,
  &--direction-horizontal &__action-list {
    &:not(:empty) {
      padding-top: space("xxsmall");
      padding-bottom: space("xxsmall");
    }
  }

  &--direction-horizontal &__meta {
    padding-bottom: space("xxsmall");
    padding-top: calc(#{space("xxsmall")} + 12px);
  }

  &--direction-horizontal &__readspeaker {
    padding-bottom: space("xxsmall");
    padding-top: space("xxsmall");
    padding-left: space("medium");
  }

  &--direction-horizontal &__meta {
    &:not(:empty) {
      padding-left: space("xsmall");
    }
  }

  &--direction-horizontal &__meta-status + &__meta-text {
    margin-left: space("xsmall");
  }

  /** Table Variant */

  &--table &__label,
  &--table &__meta {
    @include fontSize("nano");
  }

  &--table &__label {
    color: $colorGrey70;
  }

  &--table &__value-text {
    @include fontSize("milli");
  }

  &--table &__leading,
  &--table &__value,
  &--table &__label,
  &--table &__meta,
  &--table &__action-list {
    &:not(:empty) {
      padding-top: space("xsmall");
      padding-bottom: space("small");
    }
  }

  &--table &__label:not(:empty) + &__value {
    padding-left: space("medium");
  }

  &--table &__meta {
    &:not(:empty) {
      padding-left: space("medium");
    }
  }

  /** Big label */

  &--table#{&}--big-label &__label {
    @include font("heavy");
    @include fontSize("milli");
    color: $colorGrey80;
  }

  &--table#{&}--big-label &__leading,
  &--table#{&}--big-label &__value,
  &--table#{&}--big-label &__label,
  &--table#{&}--big-label &__meta,
  &--table#{&}--big-label &__action-list {
    &:not(:empty) {
      padding-top: 0;
      padding-bottom: 0;

      @include mq($from: small) {
        padding-top: space("xsmall");
        padding-bottom: space("small");
      }
    }
  }

  &--table#{&}--big-label &__label:not(:empty) + &__value {
    padding-left: space("medium");

    @include mq($to: small) {
      padding-left: 0;
    }
  }
}
